import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  DialogActions,
  CircularProgress,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Typography,
  Box,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import {
  Api,
  Info,
  Security,
  Settings,
} from "@mui/icons-material";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import queueService from "../../services/queueService";

const OfficialApiSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Nome muito curto!")
    .max(50, "Nome muito longo!")
    .required("Nome é obrigatório"),
  phoneNumberId: Yup.string()
    .required("Phone Number ID é obrigatório"),
  accessToken: Yup.string()
    .required("Access Token é obrigatório"),
  webhookVerifyToken: Yup.string()
    .required("Webhook Verify Token é obrigatório"),
  businessAccountId: Yup.string()
    .required("Business Account ID é obrigatório"),
  queueId: Yup.number()
    .required("Fila é obrigatória")
});

const initialState = {
  name: "",
  phoneNumberId: "",
  accessToken: "",
  webhookVerifyToken: "",
  businessAccountId: "",
  webhookUrl: "",
  apiVersion: "v18.0",
  isDefault: false,
  isActive: true,
  messageCategory: "service",
  queueId: "",
  maxRetries: 3,
  retryDelay: 1000,
  timeout: 30000,
  rateLimit: 1000,
  greetingMessage: "",
  outOfHoursMessage: "",
  errorMessage: "Desculpe, ocorreu um erro. Tente novamente mais tarde."
};

const OfficialApiModal = ({ open, onClose, connection, onSave }) => {
  // Remove this line:
  // const classes = useStyles();

  const [formData, setFormData] = useState(initialState);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [queues, setQueues] = useState([]); // Estado para filas
  const [loadingQueues, setLoadingQueues] = useState(false);

  // Carregar filas disponíveis
  useEffect(() => {
    const fetchQueues = async () => {
      if (open) {
        setLoadingQueues(true);
        try {
          const queueList = await queueService.getQueues();
          const normalized = Array.isArray(queueList)
            ? queueList
            : (Array.isArray(queueList?.data)
              ? queueList.data
              : (Array.isArray(queueList?.queues)
                ? queueList.queues
                : []));
          setQueues(normalized);
        } catch (error) {
          toast.error("Erro ao carregar filas");
        } finally {
          setLoadingQueues(false);
        }
      }
    };

    fetchQueues();
  }, [open]);

  useEffect(() => {
    if (connection) {
      setFormData({ ...initialState, ...connection });
    } else {
      setFormData(initialState);
    }
  }, [connection, open]);

  const handleSave = async (values) => {
    try {
      const connectionData = {
        ...values,
        type: "official",
      };

      if (connection?.id) {
        await api.put(`/api/whatsapp-connections/${connection.id}`, connectionData);
        toast.success("Conexão atualizada com sucesso!");
      } else {
        await api.post("/api/whatsapp-connections", connectionData);
        toast.success("Conexão criada com sucesso!");
      }
      
      onSave();
    } catch (err) {
      toastError(err);
    }
  };

  const handleTestConnection = async (values) => {
    setTesting(true);
    setTestResult(null);
    
    try {
      const resp = await api.post("/api/whatsapp-connections/test-official", {
        phoneNumberId: values.phoneNumberId,
        accessToken: values.accessToken,
        businessAccountId: values.businessAccountId,
        apiVersion: values.apiVersion,
      });
      
      setTestResult({
        success: true,
        message: "Conexão testada com sucesso!",
        details: resp?.data || resp,
      });
      toast.success("Teste de conexão realizado com sucesso!");
    } catch (err) {
      setTestResult({
        success: false,
        message: "Falha no teste de conexão",
        error: err?.payload?.message || err?.message,
      });
      toast.error("Falha no teste de conexão");
    } finally {
      setTesting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setFormData(initialState);
    setTestResult(null);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        style: { borderRadius: 16 },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Api color="primary" style={{ marginRight: 8 }} />
          <Typography variant="h6">
            {connection ? "Editar" : "Adicionar"} Conexão API Oficial
          </Typography>
        </Box>
      </DialogTitle>
      
      <Formik
        initialValues={formData}
        enableReinitialize={true}
        validationSchema={OfficialApiSchema}
        onSubmit={(values, actions) => {
          setTimeout(() => {
            handleSave(values);
            actions.setSubmitting(false);
          }, 400);
        }}
      >
        {({ values, touched, errors, isSubmitting, setFieldValue }) => (
          <Form>
            <DialogContent sx={{ padding: 3 }}>
              {/* Informações Básicas */}
              <Box sx={{ 
                backgroundColor: '#f5f5f5', 
                padding: 2, 
                borderRadius: 1, 
                marginBottom: 3,
                border: '1px solid #e0e0e0'
              }}>
                <Typography variant="body2" style={{ display: "flex", alignItems: "center" }}>
                  <Info style={{ marginRight: 8, fontSize: 16 }} />
                  Para usar a API oficial, você precisa ter uma conta WhatsApp Business aprovada.
                  <a href="https://developers.facebook.com/docs/whatsapp" target="_blank" rel="noopener noreferrer" style={{ marginLeft: 8 }}>
                    Saiba mais
                  </a>
                </Typography>
              </Box>

              {/* Configurações Básicas */}
              <Typography variant="h6" sx={{ 
                display: "flex", 
                alignItems: "center", 
                mb: 2, 
                mt: 2 
              }}>
                <Settings style={{ marginRight: 8 }} />
                Configurações Básicas
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Field name="name">
                    {({ field, meta }) => (
                      <TextField
                        {...field}
                        label="Nome da Conexão"
                        fullWidth
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        margin="normal"
                      />
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Field name="queueId">
                    {({ field, meta }) => (
                      <FormControl 
                        fullWidth 
                        margin="normal"
                        error={meta.touched && !!meta.error}
                      >
                        <InputLabel>Fila *</InputLabel>
                        <Select
                          {...field}
                          label="Fila *"
                          disabled={loadingQueues}
                        >
                          {loadingQueues ? (
                            <MenuItem disabled>
                              <CircularProgress size={20} sx={{ mr: 1 }} />
                              Carregando filas...
                            </MenuItem>
                          ) : (
                            queues.map((queue) => (
                              <MenuItem key={queue.id} value={queue.id}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      backgroundColor: queue.color || '#999',
                                      mr: 1
                                    }}
                                  />
                                  {queue.name}
                                </Box>
                              </MenuItem>
                            ))
                          )}
                        </Select>
                        {meta.touched && meta.error && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field name="messageCategory">
                    {({ field, meta }) => (
                      <FormControl fullWidth margin="normal" error={meta.touched && !!meta.error}>
                        <InputLabel>Tipo</InputLabel>
                        <Select {...field} label="Tipo">
                          <MenuItem value="billing">Cobrança</MenuItem>
                          <MenuItem value="otp">OTP</MenuItem>
                          <MenuItem value="service">Serviço</MenuItem>
                        </Select>
                        {meta.touched && meta.error ? <FormHelperText>{meta.error}</FormHelperText> : null}
                      </FormControl>
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined" margin="dense">
                    <InputLabel>Versão da API</InputLabel>
                    <Field
                      as={Select}
                      name="apiVersion"
                      label="Versão da API"
                    >
                      <MenuItem value="v18.0">v18.0 (Recomendado)</MenuItem>
                      <MenuItem value="v17.0">v17.0</MenuItem>
                      <MenuItem value="v16.0">v16.0</MenuItem>
                    </Field>
                  </FormControl>
                </Grid>
              </Grid>

              <Box mt={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Field
                          as={Switch}
                          name="isDefault"
                          color="primary"
                          checked={values.isDefault}
                        />
                      }
                      label="Conexão Padrão"
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Field
                          as={Switch}
                          name="isActive"
                          color="primary"
                          checked={values.isActive}
                        />
                      }
                      label="Ativa"
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider style={{ margin: "24px 0" }} />

              {/* Credenciais da API */}
              <Typography variant="h6" sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: 2, 
                fontWeight: 600 
              }}>
                <Security sx={{ marginRight: 1, fontSize: 20 }} />
                Credenciais da API
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    label="Phone Number ID"
                    name="phoneNumberId"
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    error={touched.phoneNumberId && Boolean(errors.phoneNumberId)}
                    helperText={touched.phoneNumberId && errors.phoneNumberId}
                    placeholder="Ex: 123456789012345"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    label="Access Token"
                    name="accessToken"
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    type="password"
                    error={touched.accessToken && Boolean(errors.accessToken)}
                    helperText={touched.accessToken && errors.accessToken}
                    placeholder="Seu token de acesso permanente"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    label="Business Account ID"
                    name="businessAccountId"
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    error={touched.businessAccountId && Boolean(errors.businessAccountId)}
                    helperText={touched.businessAccountId && errors.businessAccountId}
                    placeholder="Ex: 123456789012345"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    label="Webhook Verify Token"
                    name="webhookVerifyToken"
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    error={touched.webhookVerifyToken && Boolean(errors.webhookVerifyToken)}
                    helperText={touched.webhookVerifyToken && errors.webhookVerifyToken}
                    placeholder="Token para verificação do webhook"
                  />
                </Grid>
              </Grid>

              {/* URL do Webhook (somente leitura) */}
              <Box mt={2}>
                <Field
                  as={TextField}
                  label="URL do Webhook"
                  name="webhookUrl"
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  InputProps={{ readOnly: true }}
                  value={`${window.location.origin}/api/webhooks/whatsapp`}
                  helperText="Configure esta URL no seu painel do WhatsApp Business"
                />
              </Box>

              {/* Teste de Conexão */}
              <Box mt={3}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => handleTestConnection(values)}
                  disabled={testing || !values.phoneNumberId || !values.accessToken}
                  sx={{ marginTop: 2 }}
                >
                  {testing ? "Testando..." : "Testar Conexão"}
                  {testing && <CircularProgress size={20} style={{ marginLeft: 8 }} />}
                </Button>
                
                {testResult && (
                  <Box mt={2}>
                    <Chip
                      label={testResult.message}
                      color={testResult.success ? "primary" : "secondary"}
                      variant="outlined"
                    />
                    {testResult.error && (
                      <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
                        {testResult.error}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>

              <Divider style={{ margin: "24px 0" }} />

              {/* Mensagens Personalizadas */}
              <Typography variant="h6" sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: 2, 
                fontWeight: 600 
              }}>
                Mensagens Personalizadas
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    label="Mensagem de Boas-vindas"
                    name="greetingMessage"
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    margin="dense"
                    placeholder="Olá! Como posso ajudá-lo hoje?"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    label="Mensagem Fora do Horário"
                    name="outOfHoursMessage"
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    margin="dense"
                    placeholder="Obrigado por entrar em contato. Nosso horário de atendimento é..."
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions style={{ padding: "16px 24px" }}>
              <Button
                onClick={handleClose}
                color="secondary"
                disabled={isSubmitting}
                variant="outlined"
              >
                Cancelar
              </Button>
              
              <Button
                type="submit"
                color="primary"
                disabled={isSubmitting}
                variant="contained"
                sx={{ position: 'relative' }}
              >
                {connection ? "Atualizar" : "Criar"} Conexão
                {isSubmitting && (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: 'primary.main',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-12px',
                      marginLeft: '-12px'
                    }}
                  />
                )}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default OfficialApiModal;
